import type { FC, ReactElement, ReactNode} from 'react';
import { cloneElement, isValidElement, useState } from 'react';
import { Delete } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Stack } from '@mui/system';

interface DeleteActionDialogProps {
  buttonText?: string;
  buttonVariant?: 'outlined' | 'contained' | 'text';
  iconOnly?: boolean;
  buttonSize?: 'small' | 'medium' | 'large';
  bodyText?: string;
  onConfirm: (...args: any[]) => Promise<void> | void;
  children?: ReactNode;
}
const DeleteActionDialog: FC<DeleteActionDialogProps> = ({
  buttonText,
  bodyText,
  onConfirm,
  buttonVariant = 'contained',
  iconOnly = false,
  buttonSize = 'small',
  children,
}) => {
  const [open, setOpen] = useState(false);
  if (!bodyText) {
    bodyText = 'Warning: This action is irreversible. Are you absolutely certain you want to delete this?';
  }

  const dialogOnConfirm = async (e: any) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      await onConfirm();
      setOpen(!open);
    } catch (error) {
      console.error(error);
    }
  };

  const updateButtonClickHandler = (child: ReactNode): ReactNode => {
    if (!child) {
      return null;
    }

    if (isValidElement(child)) {
      const childElement = child as ReactElement;
      if (childElement.props.id === 'dialog-delete-action') {
        const updatedElement = cloneElement(childElement, {
          onClick: (e: any): void => {
            e.stopPropagation();
            e.preventDefault();
            setOpen(!open);
          },
        });
        return updatedElement;
      }

      if (childElement.props.children) {
        const updatedChildren = updateButtonClickHandler(childElement.props.children);
        return cloneElement(childElement, {}, updatedChildren);
      }
    }

    return child;
  };

  const updatedChildren = updateButtonClickHandler(children);

  return (
    <>
      {iconOnly ? (
        <Tooltip arrow placement="top" title="Delete">
          <IconButton
            aria-label={buttonText}
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen(!open);
            }}
            size={buttonSize}
            sx={{}}
          >
            <Delete fontSize={buttonSize} />
          </IconButton>
        </Tooltip>
      ) : buttonText ? (
        <Tooltip arrow placement="top" title="Delete">
          <Button
            aria-label={buttonText}
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen(!open);
            }}
            size={buttonSize}
            startIcon={<Delete fontSize={buttonSize} />}
            sx={{}}
            variant={buttonVariant}
          >
            {buttonText}
          </Button>
        </Tooltip>
      ) : (
        updatedChildren // When using, ensure the passed in button has an id of '#dialog-delete-action'
      )}

      <Dialog aria-describedby="alert-dialog-description" aria-labelledby="alert-dialog-title" open={open}>
        <DialogTitle id="alert-dialog-title" variant="h5">
          Are you sure?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{bodyText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Stack alignItems="center" direction="row" justifyContent="space-between" width="100%">
            <Button onClick={() => { setOpen(!open); }}>Cancel</Button>
            <Button autoFocus color="error" onClick={dialogOnConfirm}>
              Delete
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeleteActionDialog;
